import {
  ArrowLeft, History as HistoryIcon, MoreHorizontal, Redo2, RotateCcw, Sparkles, Undo2,
} from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/toast";
import { CadenceBuilder } from "@/features/cadence-builder";
import { CadencePreview, PlanStrip } from "@/features/cadence-preview";
import { TextPicker, Token } from "@/features/cadence-tokens";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  CADENCE_SLUG_RE, bodyFromForm, formFromBody, formProblems, type CadenceFormValues,
} from "@/lib/cadence";
import { TEMPLATES, fromTemplate, planCalls, slugFrom, type CadenceTemplate } from "@/lib/cadence-plan";
import { sameValues } from "@/lib/form";
import {
  useCadence, useCadenceCampaigns, useCadenceVersions, useCadences, useCreateCadence,
  useCreateCadenceVersion, useUpdateCadence,
} from "@/lib/queries";
import { atLeast, type Cadence } from "@/lib/types";
import { useHistory } from "@/lib/use-history";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * One cadence, as a page (ADR-066/067): the plan a lead goes down on the left, what it adds up to
 * on the right, recomputed as it changes. A new cadence starts from a template. Every edit can be
 * undone (⌘Z) until it is saved, and saving an existing cadence makes a new version: every lead on
 * it reads the new version at its next move, and nothing already scheduled is rewritten.
 *
 * Supervisors read it; admins change it.
 */
export function CadencePage({ creating = false }: { creating?: boolean }) {
  const params = useParams();
  const id = creating ? undefined : Number(params.cadenceId);
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const cadence = useCadence(Number.isFinite(id) ? id : undefined);

  if (creating) {
    return canManage ? <Editor key="new" cadence={null} canManage /> : <NotAllowed />;
  }
  if (cadence.isPending) {
    return (
      <Page>
        <div className="grid place-items-center py-24">
          <Spinner />
        </div>
      </Page>
    );
  }
  if (cadence.error || !cadence.data) {
    return (
      <Page>
        <ErrorState
          error={cadence.error}
          title="This cadence could not be loaded"
          action={
            <Button variant="outline" asChild>
              <Link to="/cadences">Back to cadences</Link>
            </Button>
          }
        />
      </Page>
    );
  }
  return <Editor key={cadence.data.id} cadence={cadence.data} canManage={canManage} />;
}

function NotAllowed() {
  return (
    <Page>
      <EmptyState title="Admins create cadences" description="A supervisor can read every cadence and see what it does." />
    </Page>
  );
}

const valuesOf = (c: Cadence): CadenceFormValues =>
  c.current_version ? formFromBody(c.current_version.body, c.name, c.slug) : { ...fromTemplate(TEMPLATES[0]!), name: c.name, slug: c.slug };

/** What decides "changed": everything but the version note. */
const comparable = (v: CadenceFormValues) => ({ ...v, note: "" });

function Editor({ cadence, canManage }: { cadence: Cadence | null; canManage: boolean }) {
  const creating = cadence === null;
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const catalog = useCadences({ enabled: creating });
  const campaigns = useCadenceCampaigns(cadence?.id);
  const versions = useCadenceVersions(cadence?.id);
  const create = useCreateCadence();
  const version = useCreateCadenceVersion();
  const update = useUpdateCadence();

  const [baseline, setBaseline] = React.useState<CadenceFormValues | null>(() => (cadence ? valuesOf(cadence) : null));
  const history = useHistory<CadenceFormValues>(baseline ?? fromTemplate(TEMPLATES[0]!));
  const values = history.value;
  const [picked, setPicked] = React.useState<CadenceTemplate | null>(null);
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [leaving, setLeaving] = React.useState<string | null>(null);
  const [retiring, setRetiring] = React.useState(false);
  const bypass = React.useRef(false);

  const readOnly = !canManage;
  const taken = React.useMemo(() => (catalog.data ?? []).map((c) => c.slug), [catalog.data]);
  const choosing = creating && picked === null;
  const dirty = creating ? !choosing : !sameValues(comparable(values), comparable(baseline ?? values));
  const allProblems = formProblems(values);
  const problems = Object.fromEntries(
    Object.entries(allProblems).filter(([key]) => submitted || (key !== "name" && key !== "slug")),
  );
  const count = Object.keys(allProblems).length;
  const busy = create.isPending || version.isPending || update.isPending;
  const inUse = campaigns.data ?? [];
  const nextVersion = (cadence?.current_version?.version_n ?? 0) + 1;

  // A version another admin saves while this page is open: said, never loaded over the edits.
  const remote = cadence ? valuesOf(cadence) : null;
  const staleBy = remote && baseline && !sameValues(comparable(remote), comparable(baseline)) ? remote : null;
  React.useEffect(() => {
    if (staleBy && !dirty) {
      setBaseline(staleBy);
      history.reset(staleBy);
    }
  }, [staleBy, dirty, history]);

  const pick = (t: CadenceTemplate) => {
    const v = fromTemplate(t);
    const next = { ...v, name: v.name, slug: slugFrom(v.name, taken) };
    setPicked(t);
    setSlugTouched(false);
    history.reset(next);
  };

  const setName = (name: string) =>
    history.set((v) => ({ ...v, name, ...(creating && !slugTouched ? { slug: slugFrom(name, taken) } : {}) }), "name");

  const save = React.useCallback(async () => {
    if (readOnly || busy || choosing) return;
    setSubmitted(true);
    setSaveError(null);
    if (Object.keys(formProblems(values)).length > 0) return;
    const body = bodyFromForm(values, cadence?.current_version?.body ?? null);
    const note = values.note.trim() || null;
    try {
      if (creating) {
        const made = await create.mutateAsync({
          slug: values.slug,
          name: values.name.trim(),
          body,
          note,
          ...(user?.tenant_id ? { tenant_id: user.tenant_id } : {}),
        });
        bypass.current = true;
        toast.success("Cadence created", `${values.name.trim()} is ready to attach on a campaign's Dialing tab.`);
        navigate(`/cadences/${made.id}`, { replace: true });
        return;
      }
      const nameChanged = values.name.trim() !== cadence.name;
      const bodyChanged = !sameValues(
        comparable({ ...values, name: "" }),
        comparable({ ...(baseline ?? values), name: "" }),
      );
      if (nameChanged) await update.mutateAsync({ id: cadence.id, name: values.name.trim() });
      if (bodyChanged) {
        const saved = await version.mutateAsync({ id: cadence.id, body, note });
        toast.success(
          `Version ${saved.current_version?.version_n ?? nextVersion} saved`,
          inUse.length
            ? `${inUse.length === 1 ? "1 campaign reads" : `${inUse.length} campaigns read`} it at each lead's next move.`
            : "No campaign runs it yet.",
        );
      } else if (nameChanged) {
        toast.success("Name saved", values.name.trim());
      }
      const kept = { ...values, note: "" };
      setBaseline(kept);
      history.reset(kept);
      setSubmitted(false);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail = (err as ApiError & { body?: { detail?: unknown } }).body?.detail;
        if (detail && typeof detail === "object" && Array.isArray((detail as { errors?: unknown }).errors)) {
          setSaveError(`The server refused it: ${(detail as { errors: string[] }).errors.join("; ")}`);
        } else {
          setSaveError(err.message);
        }
      } else {
        setSaveError("Could not save the cadence. Please try again.");
      }
    }
  }, [readOnly, busy, choosing, values, cadence, creating, create, user, toast, navigate, update, baseline, version, nextVersion, inUse.length, history]);

  // ⌘Z / ⌘⇧Z to undo and redo outside a text field; ⌘S to save anywhere.
  React.useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === "s") {
        e.preventDefault();
        void save();
        return;
      }
      const typing = e.target instanceof HTMLElement && (e.target.closest("input, textarea, [contenteditable=true]") !== null);
      if (typing) return;
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        history.undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        history.redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [readOnly, save, history]);

  // Leaving with unsaved changes asks first: a reload or a closed tab through the browser, a link
  // in the console through the dialog below.
  React.useEffect(() => {
    if (!dirty || readOnly) return;
    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const onClick = (e: MouseEvent) => {
      if (bypass.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? (e.target.closest("a[href]") as HTMLAnchorElement | null) : null;
      if (!a || a.target === "_blank" || a.origin !== window.location.origin) return;
      const to = a.pathname + a.search + a.hash;
      if (to === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setLeaving(to);
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [dirty, readOnly]);

  const retire = (active: boolean) =>
    cadence &&
    update.mutate(
      { id: cadence.id, active },
      {
        onSuccess: () => {
          setRetiring(false);
          toast.success(active ? "Cadence restored" : "Cadence retired", cadence.name);
        },
        onError: (e) => toast.error("Could not change the cadence", e instanceof ApiError ? e.message : "Please try again."),
      },
    );

  return (
    <Page className="pb-0">
      <Link to="/cadences" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
        <ArrowLeft className="size-3.5" aria-hidden />
        Cadences
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 pb-5">
        <div className="min-w-[16rem] flex-1 space-y-1.5">
          {choosing ? (
            <h1 className="text-xl font-semibold tracking-tight text-text">New cadence</h1>
          ) : readOnly ? (
            <h1 className="text-xl font-semibold tracking-tight text-text">{values.name}</h1>
          ) : (
            <input
              aria-label="Cadence name"
              placeholder="Name this cadence"
              value={values.name}
              onChange={(e) => setName(e.target.value)}
              className={cn(
                "-ml-1.5 w-full max-w-xl rounded-md border border-transparent bg-transparent px-1.5 py-0.5 text-xl font-semibold tracking-tight text-text",
                "placeholder:text-text-subtle hover:border-border focus-visible:border-accent-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/25",
                problems.name && "border-danger-9",
              )}
            />
          )}
          {problems.name && <p className="text-xs text-danger-11">{problems.name}</p>}
          {!choosing && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
            {creating && !readOnly ? (
              <Token
                label="Slug"
                text={values.slug}
                tone="muted"
                invalid={problems.slug}
                title="Slug"
                hint="How attempts and decisions name the cadence. Fixed once it is created."
                content={(close) => (
                  <TextPicker
                    label="Slug"
                    mono
                    value={values.slug}
                    validate={(s) =>
                      !CADENCE_SLUG_RE.test(s.trim())
                        ? "Lowercase letters, digits and underscores, 2 to 80, starting with a letter"
                        : taken.includes(s.trim())
                          ? "Another cadence has this slug"
                          : null
                    }
                    onChange={(slug) => {
                      setSlugTouched(true);
                      history.set((v) => ({ ...v, slug }));
                    }}
                    close={close}
                  />
                )}
              >
                <span className="font-mono">{values.slug || "slug"}</span>
              </Token>
            ) : (
              <span className="font-mono">{values.slug}</span>
            )}
            {cadence?.current_version && <Badge size="sm">v{cadence.current_version.version_n}</Badge>}
            {cadence && !cadence.active && <Badge tone="neutral" size="sm">Retired</Badge>}
            {cadence && (
              <span>
                {inUse.length === 0 ? (
                  "No campaign runs it"
                ) : (
                  <>
                    Run by{" "}
                    {inUse.slice(0, 3).map((c, i) => (
                      <React.Fragment key={c.id}>
                        {i > 0 && ", "}
                        <Link to={`/campaigns/${c.id}?tab=cadence`} className="text-text hover:underline">
                          {c.name}
                        </Link>
                      </React.Fragment>
                    ))}
                    {inUse.length > 3 && ` and ${inUse.length - 3} more`}
                  </>
                )}
              </span>
            )}
            {picked && (
              <span className="inline-flex items-center gap-1">
                <Sparkles className="size-3" aria-hidden /> From “{picked.name}”
              </span>
            )}
          </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!readOnly && !choosing && (
            <>
              <Tooltip content="Undo (⌘Z)">
                <Button variant="ghost" size="icon-sm" aria-label="Undo" disabled={!history.canUndo} onClick={history.undo}>
                  <Undo2 />
                </Button>
              </Tooltip>
              <Tooltip content="Redo (⌘⇧Z)">
                <Button variant="ghost" size="icon-sm" aria-label="Redo" disabled={!history.canRedo} onClick={history.redo}>
                  <Redo2 />
                </Button>
              </Tooltip>
            </>
          )}
          {cadence && (versions.data?.length ?? 0) > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm">
                  <HistoryIcon />
                  {versions.data!.length} version{versions.data!.length === 1 ? "" : "s"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-2">
                <ol className="max-h-80 space-y-1 overflow-y-auto">
                  {[...(versions.data ?? [])]
                    .sort((a, b) => b.version_n - a.version_n)
                    .map((v) => (
                      <li key={v.id} className="rounded-md px-2 py-1.5 hover:bg-surface-hover">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-medium text-text">Version {v.version_n}</span>
                          <span className="text-text-subtle">{formatDateTime(v.created_at)}</span>
                        </div>
                        {v.note && <p className="text-xs text-text-muted">{v.note}</p>}
                        <PlanStrip body={v.body} className="mt-1" />
                      </li>
                    ))}
                </ol>
              </PopoverContent>
            </Popover>
          )}
          {!readOnly && (cadence || picked) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="More">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {creating && (
                  <DropdownMenuItem onSelect={() => setPicked(null)}>
                    <Sparkles />
                    Start from another template
                  </DropdownMenuItem>
                )}
                {cadence &&
                  (cadence.active ? (
                    <DropdownMenuItem destructive onSelect={() => setRetiring(true)}>
                      Retire
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={() => retire(true)}>
                      <RotateCcw />
                      Restore
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>

      {staleBy && dirty && (
        <Callout tone="warning" className="mb-4">
          Someone saved a new version of this cadence while you were editing. Saving now makes yours the newest; discard to
          see theirs.
        </Callout>
      )}

      {choosing ? (
        <TemplateGallery onPick={pick} />
      ) : (
        <div className="@container">
          {/* Side by side when the page has room for both; otherwise the figures sit above the plan
              in a compact form, so a change still shows what it does without scrolling away. */}
          <div className="grid grid-cols-1 items-start gap-6 @5xl:grid-cols-[minmax(0,1fr)_380px] @5xl:gap-8">
            <div className="@5xl:hidden">
              <CadencePreview values={values} compact />
            </div>
            <CadenceBuilder values={values} set={history.set} readOnly={readOnly} problems={problems} />
            <aside className="hidden @5xl:sticky @5xl:top-6 @5xl:block">
              <CadencePreview values={values} />
            </aside>
          </div>
        </div>
      )}

      {!readOnly && !choosing && (dirty || creating) ? (
        <div className="sticky bottom-0 z-20 -mx-6 mt-8 border-t border-border bg-surface/95 px-6 py-3 backdrop-blur">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1 text-xs">
              {saveError ? (
                <span className="text-danger-11">{saveError}</span>
              ) : submitted && count > 0 ? (
                <span className="text-danger-11">
                  {count === 1 ? "One thing" : `${count} things`} to fix first: {Object.values(allProblems)[0]}
                </span>
              ) : creating ? (
                <span className="text-text-muted">Not saved yet. Nothing dials on it until a campaign picks it.</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-text-muted">
                  <span className="size-1.5 rounded-full bg-warning-9" aria-hidden />
                  Unsaved changes.{" "}
                  {inUse.length
                    ? `${inUse.length} campaign${inUse.length === 1 ? "" : "s"} will read version ${nextVersion} at each lead's next move.`
                    : "No campaign runs it yet."}
                </span>
              )}
            </div>
            <input
              aria-label={creating ? "Note" : "What changed"}
              placeholder={creating ? "Note (optional)" : "What changed? (optional)"}
              value={values.note}
              onChange={(e) => history.set((v) => ({ ...v, note: e.target.value }), "note")}
              className="h-8 w-56 rounded-md border border-border bg-surface px-2.5 text-sm text-text placeholder:text-text-subtle focus-visible:border-accent-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/25"
            />
            {!creating && (
              <Button
                variant="ghost"
                onClick={() => {
                  if (baseline) history.reset(baseline);
                  setSubmitted(false);
                  setSaveError(null);
                }}
              >
                Discard
              </Button>
            )}
            <Button variant="default" loading={busy} onClick={() => void save()}>
              {creating ? "Create cadence" : `Save as version ${nextVersion}`}
            </Button>
          </div>
        </div>
      ) : (
        <div className="h-10" />
      )}

      <ConfirmDialog
        open={leaving !== null}
        onOpenChange={(open) => !open && setLeaving(null)}
        title="Leave without saving?"
        description="The changes to this cadence are not saved. Leaving drops them."
        confirmLabel="Leave"
        destructive
        onConfirm={() => {
          const to = leaving;
          bypass.current = true;
          setLeaving(null);
          if (to) navigate(to);
        }}
      />
      <ConfirmDialog
        open={retiring}
        onOpenChange={setRetiring}
        title="Retire this cadence?"
        description={`${cadence?.name ?? "It"} keeps running on the campaigns already on it${inUse.length ? ` (${inUse.length})` : ""}, and no new campaign can pick it. It can be restored.`}
        confirmLabel="Retire"
        loading={update.isPending}
        onConfirm={() => retire(false)}
      />
    </Page>
  );
}

/** Where a new cadence starts: the shapes the floor talks about, each with what it adds up to. */
function TemplateGallery({ onPick }: { onPick: (t: CadenceTemplate) => void }) {
  return (
    <section aria-label="Start from" className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-text">Start from a shape</h2>
        <p className="text-sm text-text-muted">Pick the closest. Every call, wait and day can be changed next.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {TEMPLATES.map((t) => {
          const plan = planCalls(t.values);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onPick(t)}
              className={cn(
                "group flex flex-col gap-3 rounded-xl border border-border bg-surface p-4 text-left shadow-xs transition",
                "hover:-translate-y-0.5 hover:border-accent-7 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
              )}
            >
              <span className="space-y-1">
                <span className="block text-sm font-semibold text-text">{t.name}</span>
                <span className="block text-xs text-text-muted">{t.description}</span>
              </span>
              <PlanStrip body={bodyFromForm(t.values)} />
              <span className="mt-auto text-xs font-medium text-accent-11 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                Use this · {plan.calls.length} calls
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
